/**
 * User Recommend Handler (V1)
 * - Orchestration only (no direct DOM mutation except via render)
 */

import { notifier } from "../../core/notifier/notifier";
import { phpMaxRecommendRank } from "../../core/php-data-access";
import { z } from "zod";
import {
    GetUserRecommendMeta200Response,
    GetUserRecommendMeta401Response,
    GetUserRecommendMeta403Response,
    GetUserRecommendMeta404Response,
    GetUserRecommendMeta500Response,
    RemoveUserRecommend200Response,
    RemoveUserRecommend401Response,
    RemoveUserRecommend403Response,
    RemoveUserRecommend404Response,
    RemoveUserRecommend500Response,
    UpsertUserRecommend200Response,
    UpsertUserRecommend401Response,
    UpsertUserRecommend403Response,
    UpsertUserRecommend404Response,
    UpsertUserRecommend409Response,
    UpsertUserRecommend422Response,
    UpsertUserRecommend500Response,
} from "../../api/generated/users.zod";
import { toPositiveIntOrNull } from "../../util/id-utils";
import { userRecommendForm } from "./user-recommend-form";

interface RecommendButtonViewModel {
    rank: number;
    disabled: boolean;
    isCurrent: boolean;
}

interface RecommendViewModel {
    buttons: RecommendButtonViewModel[];
    canUnset: boolean;
}

type RecommendMetaData = NonNullable<z.infer<typeof GetUserRecommendMeta200Response>["data"]>;
type RecommendMetaResponse =
    | z.infer<typeof GetUserRecommendMeta200Response>
    | z.infer<typeof GetUserRecommendMeta401Response>
    | z.infer<typeof GetUserRecommendMeta403Response>
    | z.infer<typeof GetUserRecommendMeta404Response>
    | z.infer<typeof GetUserRecommendMeta500Response>;
type RecommendMutationResponse =
    | z.infer<typeof UpsertUserRecommend200Response>
    | z.infer<typeof UpsertUserRecommend401Response>
    | z.infer<typeof UpsertUserRecommend403Response>
    | z.infer<typeof UpsertUserRecommend404Response>
    | z.infer<typeof UpsertUserRecommend409Response>
    | z.infer<typeof UpsertUserRecommend422Response>
    | z.infer<typeof UpsertUserRecommend500Response>
    | z.infer<typeof RemoveUserRecommend200Response>
    | z.infer<typeof RemoveUserRecommend401Response>
    | z.infer<typeof RemoveUserRecommend403Response>
    | z.infer<typeof RemoveUserRecommend404Response>
    | z.infer<typeof RemoveUserRecommend500Response>;

interface UserRecommendApiLike {
    getMeta(userId: number): Promise<Response>;
    upsert(userId: number, rank: number | null): Promise<Response>;
    remove(userId: number): Promise<Response>;
}

interface UserRecommendRenderLike {
    setActions(actions: {
        onPickRank?: ((rank: number) => Promise<void> | void) | null;
        onUnset?: (() => Promise<void> | void) | null;
        onClearAll?: (() => Promise<void> | void) | null;
    }): void;
    renderBody(vm: RecommendViewModel): void;
    renderLoading(): void;
    open(): void;
    close(): void;
    setBusy(busy: boolean): void;
    setCrownState(el: HTMLElement, recommended: boolean): void;
    setCrownRank(el: HTMLElement, rank: number | null): void;
    getOverlay(): HTMLElement | null;
}

export class UserRecommendHandler {
    public api: UserRecommendApiLike;

    public render: UserRecommendRenderLike;

    public activeUserId: number | null;

    public activeCrownEl: HTMLElement | null;

    public busy: boolean;

    public constructor(deps: { api: UserRecommendApiLike; render: UserRecommendRenderLike }) {
        this.api = deps.api;
        this.render = deps.render;

        this.activeUserId = this.createActiveUserIdSlot();
        this.activeCrownEl = this.createActiveCrownSlot();
        this.busy = this.createBusyState();
    }

    public createActiveUserIdSlot(): number | null {
        return null;
    }

    public createActiveCrownSlot(): HTMLElement | null {
        return null;
    }

    public createBusyState(): boolean {
        return false;
    }

    public isSuccessResponse(value: unknown): value is 1 {
        return value === 1;
    }

    public getResponseMessage(value: unknown): string {
        if (!value || typeof value !== "object") return "";
        const message = "message" in value ? value.message : undefined;
        return typeof message === "string" ? message : "";
    }

    public isRecommendMetaResponse(value: unknown): value is RecommendMetaResponse {
        return !!value && typeof value === "object" && "success" in value;
    }

    public isRecommendMetaSuccessResponse(
        value: RecommendMetaResponse,
    ): value is z.infer<typeof GetUserRecommendMeta200Response> {
        return this.isSuccessResponse(value.success) && "data" in value && !!value.data;
    }

    public isRecommendMutationResponse(value: unknown): value is RecommendMutationResponse {
        return !!value && typeof value === "object" && "success" in value;
    }

    public isRecommendMutationSuccessResponse(
        value: RecommendMutationResponse,
    ): value is z.infer<typeof UpsertUserRecommend200Response> | z.infer<typeof RemoveUserRecommend200Response> {
        return this.isSuccessResponse(value.success);
    }

    public async handleRecommendClick(event: MouseEvent, crownEl: HTMLElement): Promise<void> {
        event.preventDefault();
        event.stopPropagation();

        const userId = toPositiveIntOrNull(crownEl.dataset.userId);
        if (userId === null) return;

        const canRecommend = crownEl.dataset?.canRecommend === "1" || userRecommendForm.hasRecommendPermission(crownEl);
        if (!canRecommend) return;

        if (this.busy || crownEl.classList.contains("loading")) return;

        this.activeUserId = userId;
        this.activeCrownEl = crownEl;

        this.render.setActions({
            onPickRank: async (rank) => await this.setRank(rank),
            onUnset: async () => await this.unsetRecommend(),
            onClearAll: async () => await this.clearAll(),
        });

        this.render.renderLoading();
        this.render.open();

        try {
            const res = await this.api.getMeta(userId);
            const json = (await res.json().catch(() => ({}))) as unknown;

            if (!res.ok || !this.isRecommendMetaResponse(json) || !this.isRecommendMetaSuccessResponse(json)) {
                notifier.error(this.getResponseMessage(json) || "user_recommend_error_load");
                this.render.close();
                return;
            }

            this.render.renderBody(this.buildViewModel(json.data));
        } catch (error) {
            console.error(error);
            notifier.error("user_recommend_error_load");
            this.render.close();
        }
    }

    public findAllCrowns(userId: number): HTMLElement[] {
        return userRecommendForm.getRecommendButtons(document, userId);
    }

    public async reloadMeta(): Promise<void> {
        if (!this.activeUserId) return;

        this.render.renderLoading();

        try {
            const res = await this.api.getMeta(this.activeUserId);
            const json = (await res.json().catch(() => ({}))) as unknown;

            if (!res.ok || !this.isRecommendMetaResponse(json) || !this.isRecommendMetaSuccessResponse(json)) {
                notifier.error(this.getResponseMessage(json) || "user_recommend_error_load");
                this.render.close();
                return;
            }

            this.render.renderBody(this.buildViewModel(json.data));
        } catch (error) {
            console.error(error);
            notifier.error("user_recommend_error_load");
            this.render.close();
        }
    }

    public async setRank(rank: number): Promise<void> {
        if (!this.activeUserId || !this.activeCrownEl) return;

        this.render.renderLoading();

        this.busy = true;
        this.render.setBusy(true);
        try {
            const res = await this.api.upsert(this.activeUserId, rank);
            const json = (await res.json().catch(() => ({}))) as unknown;
            if (!res.ok || !this.isRecommendMutationResponse(json) || !this.isRecommendMutationSuccessResponse(json)) {
                notifier.error(this.getResponseMessage(json) || "user_recommend_error_update");
                await this.reloadMeta();
                return;
            }

            const crowns = this.findAllCrowns(this.activeUserId);
            crowns.forEach((el) => {
                this.render.setCrownState(el, true);
                this.render.setCrownRank(el, rank);
            });

            this.render.close();
        } catch (error) {
            console.error(error);
            notifier.error("user_recommend_error_update");
            await this.reloadMeta();
        } finally {
            this.render.setBusy(false);
            this.busy = false;
        }
    }

    public async unsetRecommend(): Promise<void> {
        if (!this.activeUserId || !this.activeCrownEl) return;

        this.render.renderLoading();

        this.busy = true;
        this.render.setBusy(true);
        try {
            const res = await this.api.remove(this.activeUserId);
            const json = (await res.json().catch(() => ({}))) as unknown;
            if (!res.ok || !this.isRecommendMutationResponse(json) || !this.isRecommendMutationSuccessResponse(json)) {
                notifier.error(this.getResponseMessage(json) || "user_recommend_error_unset");
                await this.reloadMeta();
                return;
            }

            const crowns = this.findAllCrowns(this.activeUserId);
            crowns.forEach((el) => {
                this.render.setCrownState(el, false);
                this.render.setCrownRank(el, null);
            });

            this.render.close();
        } catch (error) {
            console.error(error);
            notifier.error("user_recommend_error_unset");
            await this.reloadMeta();
        } finally {
            this.render.setBusy(false);
            this.busy = false;
        }
    }

    public async clearAll(): Promise<void> {
        await this.unsetRecommend();
    }

    private buildViewModel(meta: RecommendMetaData): RecommendViewModel {
        const maxRank = Number(meta.max_rank || phpMaxRecommendRank());
        const usedRanks = Array.isArray(meta.used_ranks) ? meta.used_ranks : [];
        const currentRank =
            meta.current_rank === null || typeof meta.current_rank === "undefined" ? null : Number(meta.current_rank);

        const used = new Set(usedRanks.map((n) => Number(n)).filter((n) => Number.isFinite(n)));
        const buttons: RecommendButtonViewModel[] = [];

        for (let rank = 1; rank <= maxRank; rank++) {
            const isUsed = used.has(rank);
            const isCurrent = currentRank !== null && rank === currentRank;
            buttons.push({
                rank,
                disabled: isUsed && !isCurrent,
                isCurrent,
            });
        }

        return {
            buttons,
            canUnset: currentRank !== null,
        };
    }
}
